import { FunctionComponent, memo, useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ConfigContext } from '../../context';
import {
  ConfigurationLink,
  DropdownMenu,
  Popup,
  getCssVar,
  isPushNotificationsAllowed,
  layoutPath,
  useIsMyTopicsAllowed,
  useIsNarrowViewport,
  useIsNewslettersAllowed,
} from '../../shared';
import { TeaserDetails } from '../SpecialTeaser';

import dropdownMenuClasses from '../../shared/components/DropdownMenu/DropdownMenu.module.scss';

interface SettingsMenuProps {
  component?: JSX.Element;
}

export const SettingsMenu: FunctionComponent<SettingsMenuProps> = memo(({ component }) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const isNarrowViewport = useIsNarrowViewport();

  const isNewslettersAllowed = useIsNewslettersAllowed();

  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

  const [showTeaserDetails, setShowTeaserDetails] = useState<boolean>(false);

  const { config } = useContext(ConfigContext);

  const { links } = config.elements.settings;

  const isMyTopicsAllowed = useIsMyTopicsAllowed();

  const closeMenu = useCallback(() => setIsMenuOpen(false), []);

  const promoClickHandler = useCallback(() => {
    closeMenu();
    setShowTeaserDetails(true);
  }, [closeMenu]);

  const renderSettingsLinks = useMemo(
    () =>
      links.map((link) => {
        const { title, promo } = link;
        return (
          <ConfigurationLink
            key={title}
            link={link}
            labelClassName={dropdownMenuClasses['dropdown-menu__item']}
            {...(promo && { customClickHandler: promoClickHandler })}
            clickCallback={closeMenu}
            singleColor
          />
        );
      }),
    [closeMenu, links, promoClickHandler]
  );

  const pushNotificationsClickHandler = useCallback(() => {
    closeMenu();
    navigate(layoutPath('/pushNotifications'));
  }, [closeMenu, navigate]);

  const newslettersClickHandler = useCallback(() => {
    closeMenu();
    navigate(layoutPath('/newsletters'));
  }, [closeMenu, navigate]);

  const myTopicsClickHandler = useCallback(() => {
    closeMenu();
    navigate(layoutPath('/myTopics'));
  }, [closeMenu, navigate]);

  const dropdownMenuContent = useMemo(() => {
    return (
      <>
        {isPushNotificationsAllowed && (
          <button
            type={'button'}
            className={dropdownMenuClasses['dropdown-menu__item']}
            onClick={pushNotificationsClickHandler}
          >
            {t('settings.push-notifications')}
          </button>
        )}
        {isNewslettersAllowed && (
          <button
            type={'button'}
            className={dropdownMenuClasses['dropdown-menu__item']}
            onClick={newslettersClickHandler}
          >
            {t('settings.newsletters')}
          </button>
        )}
        {isMyTopicsAllowed && (
          <button
            type={'button'}
            className={dropdownMenuClasses['dropdown-menu__item']}
            onClick={myTopicsClickHandler}
          >
            {t('settings.my-topics')}
          </button>
        )}
        {component}
        {renderSettingsLinks}
      </>
    );
  }, [
    component,
    isMyTopicsAllowed,
    isNewslettersAllowed,
    myTopicsClickHandler,
    newslettersClickHandler,
    pushNotificationsClickHandler,
    renderSettingsLinks,
    t,
  ]);

  if (
    !Boolean(
      renderSettingsLinks.length ||
        isPushNotificationsAllowed ||
        isNewslettersAllowed ||
        isMyTopicsAllowed ||
        component
    )
  ) {
    return null;
  }

  return (
    <>
      <Popup
        isOpen={isMenuOpen}
        setIsOpen={setIsMenuOpen}
        iconId={'settings'}
        ariaLabel={t('common.settings')}
        color={getCssVar('--settings-icon-color')}
        hoverColor={getCssVar('--settings-icon-hover-color')}
        bodyTop={'2rem'}
        bodyRight={'0'}
        {...(isNarrowViewport && {
          bodyTop: 'var(--navbar-height)',
          bodyRight: '1rem',
          bodyPosition: 'fixed',
        })}
        body={<DropdownMenu width={'17rem'} content={dropdownMenuContent} />}
      />

      <TeaserDetails isOpen={showTeaserDetails} onClose={() => setShowTeaserDetails(false)} />
    </>
  );
});
